import React, { useState, useEffect, useCallback } from 'react'
import DashboardLayout from '../../../layouts/Dashboard'
import { Col, Row, Button, Badge, Spinner, Image, Tooltip, OverlayTrigger } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { Plus, Warning, Pencil, UserCircle } from 'phosphor-react'
import api from '../../../services/api'
import { toast } from 'react-toastify'

import styles from './styles.module.css'
import { useLoading } from '../../../hooks/LoadingContext'
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner'
import SearchInput from '../../../components/SearchInput'

interface IEmpresa {
  brand_name: string
  user_id: string
  profilePicture: { url: string }
  account_validated: boolean
}
const Empresas: React.FC = () => {
  const [companies, setcompanies] = useState<IEmpresa[]>()
  const { isLoading, setIsLoading } = useLoading()
  const [hasMoreData, setHasMoreData] = useState<boolean>(true)
  const [search, setSearch] = useState('')
  const [lastSearch, setLastSearch] = useState('')

  const navigate = useNavigate()

  const loadData = useCallback(async () => {
    const isSearchDifferent = search !== lastSearch
    if (!hasMoreData && !isSearchDifferent) return

    setIsLoading(true)

    if (isSearchDifferent) {
      setLastSearch(search)
      setHasMoreData(true)
    }

    let apiUrl = '/admin/companies?profile_picture=true&limit=12&show_invalid=true'
    if (search) apiUrl += '&brand_name=' + search

    const offset = companies && !isSearchDifferent ? companies.length : 0
    if (offset) apiUrl += '&offset=' + offset

    try {
      const { data } = await api.get(apiUrl)
      if (!data.length) {
        if (isSearchDifferent) setcompanies(data)
        setHasMoreData(false)
      } else {
        if (!companies) setcompanies(data)
        else setcompanies(isSearchDifferent ? data : companies.concat(data))
      }
    } catch (err) {
      console.error(err)
      toast.error('Falha ao buscar os dados! Atualize a página.')
    }
    setIsLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, companies, hasMoreData])

  useEffect(() => {
    if (!companies && hasMoreData) loadData()
  }, [loadData, hasMoreData, companies])

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop !==
          document.documentElement.offsetHeight ||
        isLoading
      ) {
        return
      }
      loadData()
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isLoading, loadData])

  return (
    <DashboardLayout backgroundColor="#F8F8F8" pageName="Empresas">
      <LoadingSpinner text="Buscando empresas. Aguarde..." />
      <Row style={{ marginInline: 25 }}>
        <SearchInput
          placeholder="Digite o nome da empresa"
          searchValue={search}
          setValue={setSearch}
          handleSearch={loadData}
          showOptions={false}
        />
        <Col
          style={{ display: 'flex', justifyContent: 'right' }}
          className="justify-content-md-end justify-content-center"
        >
          <Button
            className={styles.new_company_button}
            onClick={() => navigate('/admin/empresas/nova')}
          >
            <Plus
              style={{ marginRight: 4, marginBottom: 4 }}
              color="#fff"
              size={15}
              weight="bold"
            />
            Nova empresa
          </Button>
        </Col>
      </Row>
      <Row style={{ marginInline: 30, display: 'flex', justifyContent: 'left' }}>
        {isLoading && (
          <div
            style={{
              textAlign: 'center',
              marginTop: 40,
            }}
          >
            <Spinner
              animation="border"
              style={{
                color: '#053C5E',
                marginRight: 10,
              }}
            />
          </div>
        )}
        {!companies?.length && !isLoading && (
          <Badge
            bg=""
            text="white"
            style={{ textAlign: 'center', marginTop: 40, backgroundColor: '#053c5e' }}
          >
            <Warning style={{ marginBottom: 2 }} color="#FFFFFF" size={15} weight="bold" /> Nenhuma
            empresa encontrada!
          </Badge>
        )}
        {companies &&
          companies.length > 0 &&
          companies?.map((company, index) => (
            <Col key={index} style={{ padding: 8 }} className="col-12 col-md-6 col-lg-6 col-xl-4">
              <div className={styles.company_card}>
                <Col md={5} style={{ alignSelf: 'center' }} className="col-5">
                  {company.profilePicture ? (
                    <Image
                      style={{
                        objectFit: 'cover',
                        width: '100%',
                        aspectRatio: '1',
                        borderRadius: '50%',
                      }}
                      fluid={false}
                      src={company.profilePicture.url}
                    />
                  ) : (
                    <UserCircle size={'100%'} />
                  )}
                </Col>
                <Col md={7} className={`col-7 ${styles.company_info}`}>
                  <Row style={{ textAlign: 'center', margin: 0 }}>
                    <span className={styles.company_name}>{company.brand_name}</span>
                    {!company.account_validated && (
                      <div>
                        <Badge bg="danger" style={{ marginTop: 5, fontSize: 10 }}>
                          Conta não validada
                        </Badge>
                      </div>
                    )}
                  </Row>
                  <Row className={styles.btn_row}>
                    <OverlayTrigger placement="bottom" overlay={<Tooltip>Editar empresa</Tooltip>}>
                      <div
                        className={styles.edit_company_button}
                        onClick={() => navigate(`/admin/empresas/editar/${company.user_id}`)}
                      >
                        <Pencil color="#053C5E" size={22} />
                      </div>
                    </OverlayTrigger>
                  </Row>
                </Col>
              </div>
            </Col>
          ))}
      </Row>
    </DashboardLayout>
  )
}

export default Empresas
